// @ts-nocheck
// ToastHost — 전역 토스트 수신부 (lib/toast.ts CustomEvent 수신, 라우터에 1회 마운트)
// 게임/시스템 토스트를 두 표면으로 나눠 상단에 스택 표시한다.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { probeMark } from './HistoryProbe'; // 계측 빌드에서만 동작(평소 no-op)
import { TOAST_EVENT, type ToastKind, type ToastPayload } from '../lib/toast';
import { CSIcon } from '../redesign/_components/cs';
import { CastAvatar } from '../redesign/_components/cast';
import { preloadToastCast } from '../lib/preloadCast';
import '../redesign/cs-tokens.css';

type ToastItem = ToastPayload & { id: number; leaving?: boolean };
let _seq = 0;

// 위로 이만큼 밀면 닫는다. 너무 짧으면 스크롤 의도와 충돌하고, 길면 "안 닫힌다"고 느낀다.
const SWIPE_DISMISS_PX = 40;
const MAX_VISIBLE_TOASTS = 3;
// 퇴장 애니메이션 길이 — 이만큼 기다렸다 DOM에서 지운다. CSS의 sotda-toast-out과 같은 값이어야 한다.
const EXIT_MS = 200;

const DURATION: Record<ToastKind, number> = {
  levelup: 3200,
  achievement: 3200,
  attendance: 3200,
  quest: 3200,
  success: 2000,
  error: 6000,
  info: 3200,
};

type GameToastKind = Extract<ToastKind, 'levelup' | 'achievement' | 'attendance' | 'quest'>;
type SystemToastKind = Exclude<ToastKind, GameToastKind>;

// 캐릭터 배역은 승인 결과에 따라 한 줄씩 교체할 수 있게 렌더 로직과 분리한다.
const CAST_BY_KIND: Record<GameToastKind, string> = {
  levelup: 'hwangdoongi',
  achievement: 'roki',
  attendance: 'kongi',
  quest: 'bbiyong',
};

// kind별 기본 표정. 개별 호출이 payload.expr로 덮어쓸 수 있다(상황에 맞는 얼굴).
const EXPR_BY_KIND: Record<GameToastKind, string> = {
  levelup: 'celebrate',
  achievement: 'confident',
  attendance: 'confident',
  quest: 'celebrate',
};

const GAME_KINDS = new Set<ToastKind>(Object.keys(CAST_BY_KIND) as GameToastKind[]);

function SystemIcon({ kind }: { kind: SystemToastKind }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
    style: { flexShrink: 0 },
  } as const;

  if (kind === 'success') {
    return (
      <svg {...common} stroke="var(--cs-income)">
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.6 2.6L16.5 9" />
      </svg>
    );
  }

  if (kind === 'error') {
    return (
      <svg {...common} stroke="var(--cs-alert)">
        <path d="M10.3 3.8 2.5 17.3A2 2 0 0 0 4.2 20h15.6a2 2 0 0 0 1.7-2.7L13.7 3.8a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    );
  }

  return (
    <svg {...common} stroke="var(--cs-mute)">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

// 토스트 카드 1장 — 스와이프 상태를 카드마다 따로 들고 있어야 해서 컴포넌트로 분리.
function ToastCard({ toast, hidden, leaving, onDismiss, onOpen }) {
  const [dragY, setDragY] = useState(0);   // 손끝을 따라가는 이동량(위로만, 음수)
  const [pressed, setPressed] = useState(false);
  const startY = useRef(null);
  const timer = useRef(null);
  const startedAt = useRef(0);
  const remaining = useRef(Math.max(DURATION[toast.kind], toast.link ? 5000 : 0));
  const dismissed = useRef(false);
  const onDismissRef = useRef(onDismiss);
  const clickable = !!toast.link;
  const gameKind = GAME_KINDS.has(toast.kind);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    if (dismissed.current) return;
    dismissed.current = true;
    clearTimer();
    onDismissRef.current();
  }, [clearTimer]);

  const pause = useCallback(() => {
    if (timer.current === null || dismissed.current) return;
    clearTimer();
    remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
  }, [clearTimer]);

  const resume = useCallback(() => {
    if (timer.current !== null || dismissed.current) return;
    startedAt.current = Date.now();
    timer.current = window.setTimeout(() => {
      timer.current = null;
      remaining.current = 0;
      dismiss();
    }, Math.max(0, remaining.current));
  }, [dismiss]);

  useEffect(() => {
    resume();
    return clearTimer;
  }, [clearTimer, resume]);

  // 위로 미는 동안만 따라 움직인다. 아래로 미는 건 무시(스크롤 오작동 방지).
  const onTouchMove = (e) => {
    if (startY.current === null) return;
    const delta = e.touches[0].clientY - startY.current;
    setDragY(Math.min(0, delta));
  };

  const onTouchEnd = () => {
    if (dragY <= -SWIPE_DISMISS_PX) dismiss();
    else {
      setDragY(0); // 임계 미달 → 제자리 복귀
      resume();
    }
    startY.current = null;
    setPressed(false);
  };

  const onTouchCancel = () => {
    setDragY(0);
    startY.current = null;
    setPressed(false);
    resume();
  };

  return (
    <div
      className={`sotda-toast-anim${leaving ? ' is-leaving' : ''}`}
      style={{ display: hidden ? 'none' : 'block', pointerEvents: leaving ? 'none' : 'auto' }}
    >
    <div
      className="sotda-toast-card"
      onClick={clickable ? onOpen : undefined}
      onTouchStart={(e) => {
        pause();
        startY.current = e.touches[0].clientY;
        setPressed(true);
      }}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
      onMouseEnter={pause}
      onMouseLeave={resume}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, maxWidth: 340, width: 'fit-content',
        padding: gameKind ? '8px 16px 8px 8px' : '11px 16px 11px 12px',
        background: gameKind ? 'var(--cs-ink)' : 'var(--cs-surface)',
        color: gameKind ? 'var(--cs-bg)' : 'var(--cs-ink)',
        borderRadius: gameKind ? 999 : 12, boxShadow: 'var(--cs-shadow-3)',
        // 스와이프를 받으려면 항상 터치를 먹어야 한다(컨테이너는 none 유지).
        pointerEvents: 'auto', cursor: clickable ? 'pointer' : 'default',
        // 누르는 동안 살짝 눌리는 촉감 → "탭할 수 있다"를 문구 없이 전달(§G-4 #9)
        transform: `translateY(${dragY}px) scale(${pressed && clickable ? 0.97 : 1})`,
        // 드래그 중엔 transition 없이 손끝을 따라가고, 놓을 때만 부드럽게 복귀(§G-4 #6)
        transition: startY.current === null ? 'transform .18s ease, opacity .18s ease' : 'none',
        opacity: dragY < 0 ? Math.max(0, 1 + dragY / 90) : 1,
        touchAction: 'none',
      }}>
      {gameKind ? (
        <CastAvatar
          id={CAST_BY_KIND[toast.kind]}
          expr={toast.expr || EXPR_BY_KIND[toast.kind]}
          size={34}
          ring
          ringColor="rgba(228,197,122,.4)"
          bare={false}
        />
      ) : (
        <SystemIcon kind={toast.kind} />
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{toast.title}</div>
        {toast.subtitle && <div style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.3 }}>{toast.subtitle}</div>}
      </div>
      {clickable && (
        <CSIcon name="arrow" size={16} color={gameKind ? 'var(--cs-gold-on-ink)' : 'var(--cs-mute)'} />
      )}
    </div>
    </div>
  );
}

export function ToastHost() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const navigate = useNavigate();
  const location = useLocation(); // 토스트 링크가 '지금 이 화면'인지 판정(스택 중복 방지)

  // 게임 토스트의 화자는 캐릭터 얼굴이다 — 뜨는 순간 비어 있지 않도록 유휴 시간에 받아둔다.
  useEffect(() => { preloadToastCast(); }, []);

  useEffect(() => {
    function handler(e: Event) {
      const detail = (e as CustomEvent).detail as ToastPayload;
      if (!detail) return;
      const id = ++_seq;
      setToasts((prev) => [...prev, { id, ...detail }]);
    }
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, []);

  if (!toasts.length) return null;
  const hiddenCount = Math.max(0, toasts.length - MAX_VISIBLE_TOASTS);
  return (
    <div className="cs-root" data-temp="warm" style={{
      position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 12px)', left: 16, right: 16,
      zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none',
      // ⚠ .cs-root는 background:var(--cs-bg)를 칠한다(cs-tokens.css). 이 컨테이너는 좌우로 화면 폭을
      //   차지하는데 카드는 fit-content라, 상속하면 카드 양옆에 아이보리 판이 깔린다(2026-07-25 실기 발견).
      background: 'transparent',
    }}>
      <style>{`
        /* 위에서 내려왔다가 위로 되돌아간다 — 알림이 온 방향(화면 밖 위쪽)과 일치시킨다. */
        @keyframes sotda-toast-in {
          from { opacity: 0; transform: translateY(-14px) scale(.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes sotda-toast-out {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to   { opacity: 0; transform: translateY(-10px) scale(.97); }
        }
        /* 진입은 느슨하게 감속(들어오는 걸 보게), 퇴장은 짧게(사라지는 걸 기다리지 않게).
           기본값은 cubic-bezier — iOS 14~17.1과 구형 안드로이드에서도 그대로 동작한다. */
        .sotda-toast-anim {
          animation: sotda-toast-in var(--toast-in-dur, .26s) var(--toast-in-ease, cubic-bezier(.16,1,.3,1)) both;
        }
        /* 퇴장도 ease-out — ease-in은 시작이 느려 "안 사라지나?" 하는 순간을 만든다 (2026-09-11 §motion) */
        .sotda-toast-anim.is-leaving { animation: sotda-toast-out ${EXIT_MS}ms var(--cs-ease-out) both; }

        /* iOS 제어센터·알림 배너의 그 탄성 = 감쇠 스프링(목표를 살짝 지나쳤다 되돌아옴).
           cubic-bezier는 4점 곡선이라 이 왕복을 못 그린다. linear()는 스프링 방정식을
           48점으로 샘플링해 넘기므로 재현된다 — 지원 브라우저에서만 켠다.
           ζ=0.72 · response=0.42s → 오버슛 3.8%. 더 튀면 알림이 산만해진다.
           지원: Safari/iOS 17.2+ · Chrome 113+ (2026-09-02 caniuse·MDN 교차확인) */
        @supports (animation-timing-function: linear(0, 1)) {
          :root {
            --toast-in-dur: .49s;
            --toast-in-ease: linear(0, 0.0109, 0.0405, 0.0844, 0.1389, 0.2007, 0.2671, 0.3357, 0.4048, 0.4727, 0.5382, 0.6005, 0.6589, 0.7128, 0.7622, 0.8067, 0.8465, 0.8817, 0.9124, 0.9388, 0.9613, 0.9801, 0.9957, 1.0082, 1.0181, 1.0257, 1.0312, 1.0349, 1.0372, 1.0383, 1.0383, 1.0375, 1.0361, 1.0342, 1.032, 1.0295, 1.0269, 1.0243, 1.0216, 1.0191, 1.0166, 1.0143, 1.0121, 1.0101, 1.0083, 1.0067, 1.0052, 1.004, 1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .sotda-toast-card { transition: none !important; }
          .sotda-toast-anim, .sotda-toast-anim.is-leaving { animation: none !important; }
        }
      `}</style>
      {hiddenCount > 0 && (
        <div style={{
          padding: '5px 11px',
          borderRadius: 999,
          background: 'var(--cs-surface-2)',
          color: 'var(--cs-mute)',
          boxShadow: 'var(--cs-shadow-1)',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1.3,
        }}>
          이전 알림 {hiddenCount}개
        </div>
      )}
      {toasts.map((t, index) => {
        // 즉시 지우면 퇴장 애니메이션을 볼 틈이 없다 → leaving으로 표시하고 애니메이션이 끝난 뒤 제거.
        const dismiss = () => {
          setToasts((prev) => prev.map((x) => (x.id === t.id ? { ...x, leaving: true } : x)));
          window.setTimeout(
            () => setToasts((prev) => prev.filter((x) => x.id !== t.id)),
            EXIT_MS,
          );
        };
        return (
          <ToastCard
            key={t.id}
            toast={t}
            hidden={index < hiddenCount}
            leaving={t.leaving}
            onDismiss={dismiss}
            onOpen={() => {
              dismiss();
              // 같은 화면에서 뜬 토스트를 탭하면(예: 복기 화면의 '완료' 토스트 → /portfolio-analysis?focus=review)
              // push는 스택에 같은 화면을 한 장 더 쌓는다 → 뒤로가기를 눌러도 같은 화면이 다시 떠 "안 나가진다".
              // 목적지 pathname이 지금 화면과 같으면 replace로 바꿔 스택 중복을 막는다(쿼리만 갱신).
              const samePath = String(t.link).split('?')[0].split('#')[0] === location.pathname;
              probeMark(`toast open ${samePath ? 'replace' : 'push'} → ${t.link}`);
              navigate(t.link, samePath ? { replace: true } : undefined);
            }}
          />
        );
      })}
    </div>
  );
}
